'use client';

import CoreLink from '@eliadmin/core/src/components/link';
import { LOCALES } from '@eliadmin/core/src/i18n/i18n-locales';
import { usePathname } from 'next/navigation';
import { forwardRef, useLayoutEffect, useRef, type ComponentPropsWithoutRef } from 'react';
import {
  createCatalogueNavigationState,
  getCatalogueNavigation,
  shouldScrollCurrentCataloguePage,
  type PendingCatalogueNavigation,
} from '@/utils/catalogue-navigation';

const localeCodes = LOCALES.map(locale => locale.locale);
const navigation = createCatalogueNavigationState();
let removeCancellationListeners: (() => void) | undefined;

function scrollToCatalogueTop() {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
  });
}

function cancelNavigation() {
  navigation.cancel();
  removeCancellationListeners?.();
  removeCancellationListeners = undefined;
}

function beginNavigation(pending: PendingCatalogueNavigation) {
  cancelNavigation();
  navigation.begin(pending);

  // Any later navigation intent invalidates this one. In particular, back/forward
  // must retain native restoration rather than consume a delayed product marker.
  const cancelOnAnchorClick = (event: MouseEvent) => {
    if (
      event.button !== 0 ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      !(event.target instanceof Element)
    )
      return;
    const anchor = event.target.closest('a');
    if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download'))
      return;
    cancelNavigation();
  };

  document.addEventListener('click', cancelOnAnchorClick, true);
  document.addEventListener('submit', cancelNavigation, true);
  window.addEventListener('popstate', cancelNavigation);
  window.addEventListener('pagehide', cancelNavigation);
  removeCancellationListeners = () => {
    document.removeEventListener('click', cancelOnAnchorClick, true);
    document.removeEventListener('submit', cancelNavigation, true);
    window.removeEventListener('popstate', cancelNavigation);
    window.removeEventListener('pagehide', cancelNavigation);
  };
}

/** Saprem catalogue composition: retain core href/prefetch, own just ready scrolling. */
export const CatalogueLink = forwardRef<
  HTMLAnchorElement,
  Omit<ComponentPropsWithoutRef<typeof CoreLink>, 'key'>
>(function CatalogueLink({ onClick, onNavigate, scroll, ...props }, ref) {
  const candidate = useRef<PendingCatalogueNavigation | null>(null);
  const hasHash = typeof props.href === 'string' ? props.href.includes('#') : !!props.href.hash;

  return (
    <CoreLink
      {...props}
      ref={ref}
      scroll={hasHash ? scroll : false}
      onClick={event => {
        onClick?.(event);
        const interaction = {
          button: event.button,
          defaultPrevented: event.defaultPrevented,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          target: event.currentTarget.target,
          download: event.currentTarget.hasAttribute('download'),
        };
        candidate.current = null;
        if (
          scroll !== false &&
          shouldScrollCurrentCataloguePage(
            window.location.href,
            event.currentTarget.href,
            interaction,
            localeCodes,
          )
        ) {
          event.preventDefault();
          cancelNavigation();
          scrollToCatalogueTop();
          return;
        }
        candidate.current =
          scroll === false
            ? null
            : getCatalogueNavigation(
                window.location.href,
                event.currentTarget.href,
                interaction,
                localeCodes,
              );
      }}
      onNavigate={event => {
        let prevented = false;
        onNavigate?.({
          preventDefault: () => {
            prevented = true;
            event.preventDefault();
          },
        });
        const pending = candidate.current;
        candidate.current = null;
        if (!prevented && pending) beginNavigation(pending);
      }}
    />
  );
});

/** Mount beside visible ready content, using its model slug, not the current URL. */
export function CatalogueNavigationReady({ slug }: { slug: string }) {
  const pathname = usePathname();

  useLayoutEffect(() => {
    if (!navigation.consume(window.location.href, slug, localeCodes)) {
      if (!navigation.hasPending()) cancelNavigation();
      return;
    }
    cancelNavigation();
    scrollToCatalogueTop();
  }, [pathname, slug]);

  return null;
}
